import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UNITS, currency } from "@/lib/portalConstants";
import { Plus, Pencil, Trash2, Loader2, Tags } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { name: "", description: "", unit: "hour", rate: 0 };

export default function ServicesCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const r = await api.get("/portal/service-items");
    setItems(r.data);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (i) => { setForm({ ...empty, ...i }); setEditId(i.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Please name the service.");
    const payload = { ...form, rate: parseFloat(form.rate) || 0 };
    try {
      if (editId) await api.put(`/portal/service-items/${editId}`, payload);
      else await api.post("/portal/service-items", payload);
      toast.success(editId ? "Service updated" : "Service added");
      setOpen(false);
      load();
    } catch {
      toast.error("Could not save service.");
    }
  };
  const remove = async (id) => {
    await api.delete(`/portal/service-items/${id}`);
    toast.success("Service removed");
    load();
  };

  return (
    <div className="space-y-8" data-testid="services-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl text-charcoal">Services & Pricing</h1>
          <p className="text-charcoal-soft mt-1">Your menu of services — used to build invoices.</p>
        </div>
        <button onClick={openNew} data-testid="add-service-button" className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-5 py-3 text-sm font-medium hover:bg-sage-dark transition-colors">
          <Plus className="h-4 w-4" /> Add Service
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sage" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-14 text-center">
          <Tags className="h-10 w-10 text-sage/50 mx-auto mb-4" />
          <p className="text-charcoal-soft">No services yet. Add services like "Personal Care" or "Companion Visit" with your rates.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((i) => (
            <div key={i.id} data-testid={`service-card-${i.id}`} className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-6 group">
              <div className="flex items-start justify-between">
                <h3 className="font-serif text-xl text-charcoal">{i.name}</h3>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(i)} data-testid={`edit-service-${i.id}`} className="p-1.5 text-charcoal/40 hover:text-charcoal"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(i.id)} data-testid={`delete-service-${i.id}`} className="p-1.5 text-charcoal/40 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {i.description && <p className="text-sm text-charcoal-soft mt-1">{i.description}</p>}
              <p className="mt-4 font-serif text-2xl text-sage-700">{currency(i.rate)} <span className="text-sm text-charcoal-soft font-sans">/ {i.unit}</span></p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-sand max-w-md" data-testid="service-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit Service" : "Add Service"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Service name</Label><Input data-testid="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Personal Care" className="mt-1.5 bg-white" /></div>
            <div><Label>Description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-white resize-none" rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rate ($)</Label>
                <Input data-testid="service-rate" type="number" min="0" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="mt-1.5 bg-white" />
              </div>
              <div>
                <Label>Per</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="service-unit" className="mt-1.5 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="save-service" className="w-full rounded-full bg-sage text-white py-3 font-medium hover:bg-sage-dark transition-colors">{editId ? "Save Changes" : "Add Service"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
